// Config
import './config/mapbox_access_token';
import { CONSTANTS } from './config/constants';

// Lib
import BrowserSupport from './lib/browser_support';
import RespWeb from './lib/resp_web';
import LayerUtils from './lib/layer_utils';
import Utils from './lib/utils';
import MapMenu from './lib/map_menu'; // calls addListeners()
import Dialog from './lib/dialog'; // calls addListeners()
import Bookmarks from './lib/bookmarks'; // calls addListeners()
import Search from './lib/search'; // calls addListeners()
import UiInteraction from './lib/ui_interaction';

// Models
import MapObject from './models/map_object';
import MapView from './models/map_view';

// Layers
import BcycleLayer from './layers/bcycle_layer';
import BusRouteLayer, { loadVehiclePositions } from './layers/bus_route_layer';
import DiningLayer from './layers/dining_layer';
import GenderNeutralRestroomsLayer from './layers/gender_neutral_restrooms_layer';
import LibrariesLayer from './layers/libraries_layer';
import InfolabsLayer from './layers/infolabs_layer';
import ParkingLayer from './layers/parking_layer';
import TagLayer from './layers/tag_layer';

// Views
import './views/help_view'; // calls addListeners()

// Set up W namespace bindings
W.LayerUtils = LayerUtils;
W.resp = RespWeb;
W.MapView = MapView;
W.loadVehiclePositions = loadVehiclePositions;
W.vehicles = [];

// Register layers
W.layers.bcycle = new BcycleLayer();
W.layers.dining = new DiningLayer();
W.layers.gender_neutral_restrooms = new GenderNeutralRestroomsLayer();
W.layers.libraries = new LibrariesLayer();
W.layers.infolabs = new InfolabsLayer();
W.layers.parking = new ParkingLayer();

// Register bus route layers
['80', '81', '82', '84'].forEach((route_id) => {
	W.layers['bus_route_layer_' + route_id] = new BusRouteLayer(route_id);
});

// Register tag layers from server-injected W.tags
if (W.tags) {
	W.tags.forEach((tag) => {
		W.layers[tag.key] = new TagLayer(tag);
	});
}

// Map initialization
if (!mapboxgl.supported()) {
	BrowserSupport.init();
} else {
	let map = new mapboxgl.Map({
		container: 'map-div',
		style: CONSTANTS.map_style,
		config: {
			basemap: {
				showPointOfInterestLabels: false,
			},
		},
		center: CONSTANTS.map_center,
		zoom: 16,
		minZoom: 13,
		maxZoom: 20,
		maxBounds: [
			[-89.6, 42.96],
			[-89.3, 43.2],
		],
		scrollZoom: true,
		pitchWithRotate: false,
		dragRotate: false,
		hash: false,
		trackResize: true,
	});
	W.map = map;
	W.map.touchZoomRotate.disableRotation();

	// our controls get added when mapInit() runs
	let navigationControl = new mapboxgl.NavigationControl({ showCompass: false });
	let geoLocationControl = new mapboxgl.GeolocateControl({
		positionOptions: {
			enableHighAccuracy: true,
		},
		trackUserLocation: true,
	});

	let layerIdsFromMapbox = [
		'bus-stops',
		'uw-parking-underground',
		'uw-buildings',
		'uw-buildings-partial',
		'uw-parking-lots',
		'uw-natural-areas',
		'uw-labels',
	];
	// Layer marker clicks are now handled by DOM marker event listeners
	// so only Mapbox style layers need click handling here
	let allLayerIds = layerIdsFromMapbox;

	W.map.on('load', () => {
		// open popup for initObj param
		if (W.init_obj) {
			let map_object = new MapObject(W.init_obj.id);
			UiInteraction.fetchAndAddFeature(map_object);
			W.map.panTo(W.init_obj.lnglat, { animate: false, offset: [0, 100] });
		}

		// Restore the map state
		if (W.state) {
			const map_view = new MapView(W.state);
			map_view.restore();
		}

		// set fill paint style for highlighted features
		// dependent on highlighted feature state
		[
			{ id: 'uw-buildings', color: '#9b0000' },
			{ id: 'uw-parking-lots', color: '#7cb2e1' },
			{ id: 'uw-natural-areas', color: '#059b00' },
		].forEach((layer) => {
			const default_building_fill_color = W.map.getPaintProperty(layer.id, 'fill-color');
			W.map.setPaintProperty(layer.id, 'fill-color', [
				'case',
				['boolean', ['feature-state', 'highlighted'], false],
				layer.color,
				default_building_fill_color,
			]);
			W.map.setPaintProperty(layer.id, 'fill-opacity', [
				'case',
				['boolean', ['feature-state', 'highlighted'], false],
				0.75,
				1.0,
			]);
		});
	});

	// handle cursor on hover of map objects
	W.map.on('mouseenter', allLayerIds, function () {
		W.map.getCanvas().style.cursor = 'pointer';
	});
	W.map.on('mouseleave', allLayerIds, function () {
		W.map.getCanvas().style.cursor = '';
	});

	W.map.on('click', allLayerIds, async (e) => {
		const mapboxFeature = await e.features[0];
		UiInteraction.click(mapboxFeature);

		if (typeof gtag !== 'undefined') {
			let type = 'mouse';
			if (e.originalEvent.pointerType !== 'mouse') {
				type = 'touch';
			}
			gtag('event', 'single_click_on_layer', { type: type });
		}
	});

	// track double clicks
	W.map.on('dblclick', allLayerIds, async (e) => {
		if (typeof gtag !== 'undefined') {
			gtag('event', 'double_click', { withShiftKey: e.originalEvent.shiftKey });
		}
	});

	// hide the mobile menu if touch-dragging the map
	W.map.on('dragstart', MapMenu.hide);

	// track zoom and hide bus route stops below zoom 16
	map.on('zoomend', function () {
		const opacity = map.getZoom() > 15 ? 1 : 0;
		const body = document.querySelector('body');
		body.style.setProperty('--bus-stop-marker-opacity', opacity);
	});

	const mapInit = () => {
		const position = W.resp.mediumOrLess() ? 'bottom-left' : 'top-right';
		W.resp.setMapMenu();

		// mapboxgl does not provide way to change control position
		// so we need to remove and re-add if they have been added already
		if (W.map.hasControl(navigationControl)) {
			W.map.removeControl(navigationControl);
		}
		W.map.addControl(navigationControl, position);

		if (W.map.hasControl(geoLocationControl)) {
			W.map.removeControl(geoLocationControl);
		}
		W.map.addControl(geoLocationControl, position);

		// initialize W.popups
		W.popups = [];
	};

	// escape key closes popup if one is open (and the map dialog is not open)
	window.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && !document.getElementById('map-dialog').open) {
			if (W.popups.length > 0) {
				UiInteraction.clearAllPopups();
				// focus the search box if not mobile
				if (!Utils.hasTouch()) {
					document.getElementById('search_input').focus();
				}
			}
		}
	});

	// Things that happen on window resize
	window.addEventListener('resize', () => {
		W.map.resize();
		mapInit();
	});

	// init the map state
	mapInit();

	// map UI event handlers and analytics
	document.addEventListener('DOMContentLoaded', () => {
		// Analytics for standalone; i.e. "Save to home" on mobile device
		if (typeof window.navigator.standalone !== 'undefined') {
			if (typeof gtag !== 'undefined') {
				gtag('event', 'load', {
					event_category: 'Standalone',
					event_label: navigator.standalone ? 'true' : 'false',
				});
			}
		}

		// open dialog on page load, e.g.
		// dialog body needs to be added in application layout
		// const covid_view = new CovidView();
		// covid_view.load();

		W.MapView.addListeners();

		// focus the search box if not mobile
		if (!Utils.hasTouch()) {
			document.getElementById('search_input').focus();
		}
	});
}
